import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Calendar, Users, Send, Sparkles, X,
  Plus, DollarSign, CheckCircle, PhoneCall, TrendingUp,
  BarChart3, Clock, Target, Timer, ArrowUp, ArrowDown,
  MessageCircle, ChevronRight, Inbox
, Banknote, AlertTriangle } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import { STAGE_COLORS, COLORS } from '../../lib/constants';
import { formatCurrency, isWonStage, safeAmount, formatDate, toLocalDateString } from '../../utils/helpers';
import { handleAIQuery } from '../../services/ai';
import { useLanguage } from '../../contexts/LanguageContext';
// leadsService import removed: server-side RBAC now ensures leads prop only contains current user's leads

export default function EmployeeView({
  leads,
  currentUser,
  onSelectLead,
  onLeadViewed,
  onStageChange,
  onAddNote,
  onShowNewLead,
  isLoading,
  newLeadIds = [],
  unreadCount = 0,
  fetchAnalytics
}) {
  const { language, t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const [aiQuery, setAiQuery] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [analyticsData, setAnalyticsData] = useState(null);

  React.useEffect(() => {
    if (fetchAnalytics) {
      fetchAnalytics({ dateRange }).then(data => {
        if (data) setAnalyticsData(data);
      });
    }
  }, [dateRange, fetchAnalytics]);

  // Update local state when leads prop changes (from real-time socket updates)
  const [dashboardLeads, setDashboardLeads] = useState(leads);
  React.useEffect(() => {
    const transformed = (leads || []).map(lead => ({
      ...lead,
      clientName: lead.contact ? [lead.contact.firstName, lead.contact.lastName].filter(Boolean).join(' ') : lead.title || 'Unknown',
      stage: lead.status,
      amount: lead.finalAmount || lead.quotationAmount || lead.clientBudget || 0,
      inquiryDate: toLocalDateString(lead.createdAt),
      manager: lead.assignee ? lead.assignee.username : 'Unassigned',
      phone: lead.contact?.phone || '',
      source: lead.source?.name || ''
    }));
    setDashboardLeads(transformed);
  }, [leads]);

  const today = toLocalDateString(new Date());

  // Since the server already filters leads to only this user's leads (RBAC),
  // all leads in the prop are already "my leads".
  const myLeads = useMemo(() => {
    return (dashboardLeads || [])
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [dashboardLeads]);

  const activeLeads = useMemo(() => {
    return myLeads.filter(l => l.stage !== 'Lost' && !isWonStage(l.stage));
  }, [myLeads]);

  const stats = useMemo(() => {
    if (!analyticsData) {
      return {
        total: 0,
        wonInPeriod: 0,
        revenueInPeriod: 0,
        conversion: 0,
        callsToday: 0,
        pipelineRevenue: 0,
        bookingsConfirmed: 0,
        rank: 'N/A',
        avgResponseTime: null,
        trendData: [],
        currentPeriodLeads: []
      };
    }

    const callsToday = activeLeads.filter(l =>
      (l.nextCallDate && l.nextCallDate.startsWith(today)) ||
      (l.followUpDate && l.followUpDate.startsWith(today)) ||
      (l.siteVisitDate && l.siteVisitDate.startsWith(today) && l.siteVisitDate !== '-')
    ).length;

    // Calculate won count from leadsByStage — same source as the Lead Conversion Funnel
    // This ensures Quick Stats "Won" matches the funnel "Won" number exactly
    const wonFromStage = (() => {
      if (!analyticsData.leadsByStage) return analyticsData.bookedPeriodLeads;
      let total = 0;
      Object.entries(analyticsData.leadsByStage).forEach(([stageName, value]) => {
        if (isWonStage(stageName)) total += value;
      });
      return total;
    })();

    return {
      total: analyticsData.periodLeads,
      wonInPeriod: wonFromStage,
      revenueInPeriod: analyticsData.revenueInPeriod,
      conversion: analyticsData.conversionRate,
      callsToday,
      pipelineRevenue: analyticsData.pipelinePotential,
      bookingsConfirmed: wonFromStage,
      rank: Object.values(analyticsData.employeeStats || {})[0]?.rank || 'N/A',
      avgResponseTime: Object.values(analyticsData.employeeStats || {})[0]?.avgResponseTime || null,
      trendData: analyticsData.trendData || [],
      currentPeriodLeads: analyticsData.drilldownLeads || []
    };
  }, [analyticsData, activeLeads, today]);

  // Funnel data for my leads
  const funnelStages = ['New', 'Contacted', 'Quoted', 'Site Visited', 'Won'];
  const funnelData = useMemo(() => {
    if (!analyticsData?.leadsByStage) return [];
    const counts = {};
    funnelStages.forEach(s => (counts[s] = 0));
    Object.entries(analyticsData.leadsByStage).forEach(([stageName, value]) => {
      let s = stageName === 'New Lead' ? 'New' : stageName;
      if (isWonStage(stageName)) s = 'Won';
      if (counts[s] !== undefined) counts[s] += value;
    });
    return funnelStages.map((s, i) => ({
      name: s.replace(' Scheduled', ''),
      originalStage: s,
      value: counts[s]
    }));
  }, [analyticsData]);

  // Upcoming calls
  const upcomingCalls = useMemo(() => {
    return activeLeads
      .filter(l => l.nextCallDate && new Date(l.nextCallDate) >= new Date(today))
      .sort((a, b) => new Date(a.nextCallDate) - new Date(b.nextCallDate))
      .slice(0, 5);
  }, [activeLeads, today]);

  // Site visits scheduled (leads with a scheduled siteVisitDate that are not yet marked as 'Site Visited')
  const siteVisits = useMemo(() => {
    const isValidDateStr = (d) => !!d && d !== '-' && d !== 'Invalid Date' && !isNaN(new Date(d).getTime());
    return activeLeads
      .filter(l => isValidDateStr(l.siteVisitDate) && l.stage !== 'Site Visited' && !l.siteVisited && !isWonStage(l.stage) && l.stage !== 'Lost')
      .sort((a, b) => new Date(a.siteVisitDate) - new Date(b.siteVisitDate))
      .slice(0, 5);
  }, [activeLeads]);

  const handleAiSubmit = async () => {
    if (!aiQuery.trim()) return;
    const userMessage = aiQuery.trim();
    setAiQuery('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiLoading(true);
    const response = await handleAIQuery(userMessage, currentUser, language);
    setAiMessages(prev => [...prev, { role: 'assistant', content: response }]);
    setAiLoading(false);
  };

  return (
    <div className="space-y-4 md:space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {t('salesDashboard')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('welcomeBack')},{' '}
            <span className="font-semibold text-gray-900">{currentUser?.name}</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex bg-white rounded-xl shadow-sm border p-1">
            <button
              onClick={() => setDateRange('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'week' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('week')}
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'month' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('month')}
            </button>
            <button
              onClick={() => setDateRange('quarter')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'quarter' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('quarter')}
            </button>
            <button
              onClick={() => setDateRange('year')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'year' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              {t('year')}
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === 'all' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                }`}
            >
              All
            </button>
          </div>
          <button
            onClick={onShowNewLead}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-all shadow-sm w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            <span>{t('addLead')}</span>
          </button>
        </div>
      </div>

      {/* Stats Grid — same style as OwnerDashboard */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4">{t('quickStats')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">

          {/* Won This Month */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <CheckCircle className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('won')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.wonInPeriod}</h3>
            </div>
          </div>

          {/* My Leads */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('myLeads')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.total}</h3>
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-purple-100 text-purple-600 rounded-xl">
                <Target className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('conversionRate')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.conversion}%</h3>
            </div>
          </div>

          {/* Calls Today */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-pink-100 text-pink-600 rounded-xl">
                <PhoneCall className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('callsToday')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{stats.callsToday}</h3>
            </div>
          </div>

          {/* Avg Response Time */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-cyan-100 text-cyan-600 rounded-xl">
                <Timer className="w-5 h-5" />
              </div>
              {stats.avgResponseTime !== null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${stats.avgResponseTime <= 30 ? 'bg-green-100 text-green-700'
                    : stats.avgResponseTime <= 120 ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                  {stats.avgResponseTime <= 30 ? t('excellent')
                    : stats.avgResponseTime <= 120 ? t('good')
                      : t('needsImprovement')}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('avgResponseTime')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">
                {stats.avgResponseTime !== null
                  ? stats.avgResponseTime < 60
                    ? `${stats.avgResponseTime}m`
                    : `${Math.round(stats.avgResponseTime / 60)}h`
                  : '—'}
              </h3>
            </div>
          </div>
        
          {/* Pipeline Value (CRM) */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                <TrendingUp className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title="Pipeline Value (CRM)">{t('pipelineValue') || 'Pipeline Value (CRM)'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(analyticsData?.pipelinePotential || 0)}</h3>
            </div>
          </div>
        </div>
      </div>
      {/* Charts Row 1 */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue & Leads Trend */}
        <div className="lg:col-span-2 bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm md:text-base">
            <BarChart3 className="w-4 h-4" /> {t('leadsTrend') || 'Leads Trend'}
          </h3>
          <div className="h-48 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={30} />
                <Tooltip
                  formatter={(value, name) => [`${value} ${t('leads').toLowerCase()}`, t('leads')]}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="leads"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.1}
                  name={t('leads')}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* My Lead Funnel */}
        <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 text-sm md:text-base">
            {t('leadConversionFunnel')}
          </h3>
          <div className="space-y-2">
            {funnelData.map((item, i) => {
              const maxVal = Math.max(...funnelData.map(d => d.value), 1);
              const width = (item.value / maxVal) * 100;
              const colors = STAGE_COLORS[item.originalStage] || STAGE_COLORS['New'] || { bg: 'bg-blue-100', text: 'text-blue-700' };

              return (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-16 md:w-24 text-[10px] md:text-xs text-gray-600 truncate">{item.name}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 md:h-6 overflow-hidden">
                    <div
                      className={`h-full ${colors.bg} flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(width, 10)}%` }}
                    >
                      <span className={`text-[10px] md:text-xs font-bold ${colors.text}`}>{item.value}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Charts Row 2: Active Leads, Upcoming Calls, Site Visits */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* My Active Leads */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-blue-600" />
              {t('myActiveLeads')}
            </h3>
            <button
              onClick={() => navigate('/leads')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll')} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {activeLeads.slice(0, 5).map(lead => {
              const createdAt = new Date(lead.createdAt || 0);
              const diffDays = Math.ceil(Math.abs(new Date() - createdAt) / (1000 * 60 * 60 * 24));
              const timeAgo = diffDays <= 1 ? '1 day ago' : `${diffDays} days ago`;
              const stageColors = STAGE_COLORS[lead.stage] || {};

              return (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelectLead && onSelectLead(lead)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[11px] flex-shrink-0">
                      {lead.clientName?.substring(0, 2).toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-[13px] truncate">{lead.clientName}</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 truncate">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" /> {lead.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${stageColors.bg || 'bg-gray-100'} ${stageColors.text || 'text-gray-600'}`}>
                      {lead.stage}
                    </span>
                    <span className="text-[10px] text-gray-400">{timeAgo}</span>
                  </div>
                </div>
              );
            })}
            {activeLeads.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-xs">
                {t('noLeadsFound')}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Calls */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <PhoneCall className="w-4 h-4 text-green-600" />
              {t('upcomingCalls')}
            </h3>
            <button
              onClick={() => navigate('/call-list?filter=today')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll')} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {upcomingCalls.length > 0 ? upcomingCalls.map(call => {
              const stageColors = STAGE_COLORS[call.stage] || {};
              return (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelectLead && onSelectLead(call)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-1.5 bg-green-100 text-green-600 rounded-lg flex-shrink-0">
                      <PhoneCall className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-[13px] truncate">{call.clientName}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${stageColors.bg || 'bg-gray-100'} ${stageColors.text || 'text-gray-600'}`}>
                        {call.stage}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] font-bold text-gray-700 flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" /> {call.nextCallTime || 'TBD'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{call.nextCallDate}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-gray-400 text-xs">
                {t('noUpcomingCalls')}
              </div>
            )}
          </div>
        </div>

        {/* Site Visits Scheduled */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-purple-600" />
              {t('siteVisitsScheduled') || 'Site Visits Scheduled'}
            </h3>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {siteVisits.length > 0 ? siteVisits.map(visit => {
              const stageColors = STAGE_COLORS[visit.stage] || {};
              return (
                <div
                  key={visit.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onSelectLead && onSelectLead(visit)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg flex-shrink-0">
                      <Calendar className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-[13px] truncate">{visit.clientName}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${stageColors.bg || 'bg-gray-100'} ${stageColors.text || 'text-gray-600'}`}>
                        {visit.stage}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] font-bold text-gray-700 flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" /> {visit.siteVisitTime || visit.eventType || 'TBD'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {visit.siteVisitDate ? formatDate(visit.siteVisitDate) : formatDate(visit.createdAt)}
                    </p>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-gray-400 text-xs">
                {t('noSiteVisits') || 'No site visits scheduled.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Assistant — same style as OwnerDashboard bottom bar */}
      <div className="bg-white rounded-xl border shadow-sm p-4 md:p-5">
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-sm md:text-base">
          <Sparkles className="w-4 h-4 text-purple-500" />
          {t('aiAssistant')}
        </h3>

        {/* AI Response */}
        {aiMessages.length > 0 && aiMessages[aiMessages.length - 1].role === 'assistant' && (
          <div className="mb-3 bg-gray-50 border border-gray-200 rounded-xl p-4 relative">
            <button
              onClick={() => setAiMessages([])}
              className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
            <p className="text-sm text-gray-700 leading-relaxed pr-6">
              {aiMessages[aiMessages.length - 1].content}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Sparkles className="w-4 h-4 text-purple-400" />
            </div>
            <input
              type="text"
              className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all"
              placeholder={t('typeQuestion')}
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAiSubmit()}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <button
            onClick={handleAiSubmit}
            disabled={aiLoading || !aiQuery.trim()}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50 transition-colors font-semibold flex items-center gap-2 text-sm shadow-sm"
          >
            {aiLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">{t('send')}</span>
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
}
